import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, Search } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { format } from 'date-fns';

import { useRouter } from 'src/hooks/useRouter';
import { routesMap } from 'src/routes';
import { formatPrice } from 'src/helpers/formatting';
import {
  PriceTraceData,
  PriceTraceItem,
  PriceTracePromotion,
  PriceTraceStep,
  priceTypeLabels,
  usePriceTrace,
} from 'src/entities/price-trace';

type QueryValue = string | string[] | undefined;

const first = (value: QueryValue) =>
  (Array.isArray(value) ? value[0] : value) ?? '';

const rub = (value: number | null | undefined) =>
  value == null ? '—' : `${formatPrice(value / 100)} ₽`;

const deltaLabel = (value: number | null | undefined) => {
  if (value == null) return null;
  if (value === 0) return '0';
  return `${value < 0 ? '−' : '+'}${formatPrice(Math.abs(value) / 100)} ₽`;
};

const formatDay = (value?: string | null) =>
  value ? format(new Date(value), 'dd.MM.yyyy') : null;

const isValidProductCode = (code: string) => /^\d+$/.test(code);

interface FormValues {
  product: string;
  outlet: string;
  warehouse: string;
}

interface TraceFormProps {
  product: string;
  outlet: string;
  warehouse: string;
  onChange: (values: FormValues) => void;
  onSubmit: () => void;
  onReset: () => void;
}

const TraceForm: React.FunctionComponent<TraceFormProps> = ({
  product,
  outlet,
  warehouse,
  onChange,
  onSubmit,
  onReset,
}) => {
  const canSubmit = isValidProductCode(product.trim()) && outlet.trim().length > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (canSubmit) onSubmit();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <TextField
        label="Код товара (1С)"
        value={product}
        onChange={event =>
          onChange({ product: event.target.value, outlet, warehouse })
        }
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        placeholder="например, 196986"
        sx={{ minWidth: 200 }}
      />
      <TextField
        label="Торговая точка — vendor id"
        value={outlet}
        onChange={event =>
          onChange({ product, outlet: event.target.value, warehouse })
        }
        placeholder="например, 0001100062_7310"
        helperText="Vendor id виден у контрагента в таблице «Торговые точки» или в заказе"
        sx={{ minWidth: 280, flexGrow: 1 }}
      />
      <TextField
        label="Склад (необязательно)"
        value={warehouse}
        onChange={event =>
          onChange({ product, outlet, warehouse: event.target.value })
        }
        placeholder="Все склады региона"
        helperText="vendor id склада — иначе все склады складского региона точки"
        sx={{ minWidth: 200 }}
      />
      <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
        <Button
          type="submit"
          variant="contained"
          startIcon={<Search />}
          disabled={!canSubmit}
        >
          Показать трассировку
        </Button>
        <Button onClick={onReset}>Сбросить</Button>
      </Box>
    </Box>
  );
};

const DeltaChip: React.FunctionComponent<{ delta?: number | null }> = ({
  delta,
}) => {
  const label = deltaLabel(delta);
  if (label == null) return null;
  const direction = delta == null || delta === 0 ? 'default' : delta < 0 ? 'success' : 'error';
  return (
    <Chip
      size="small"
      label={`изменение: ${label}`}
      color={direction}
      variant={delta === 0 ? 'outlined' : 'filled'}
    />
  );
};

const StepRow: React.FunctionComponent<{
  step: PriceTraceStep;
  index: number;
  promotion?: PriceTracePromotion | null;
  onOpenPromotion: (id: number | string) => void;
}> = ({ step, index, promotion, onOpenPromotion }) => {
  const applied = Boolean(step.applied);
  const title = step.title || step.modifier || `Шаг ${index + 1}`;
  const promotionTitle =
    step.ref_type === 'promotion' &&
    step.ref_id != null &&
    promotion &&
    promotion.id === step.ref_id
      ? promotion.title
      : null;

  return (
    <Accordion
      disableGutters
      sx={{
        mb: 0.75,
        boxShadow: 'none',
        border: 1,
        borderStyle: applied ? 'solid' : 'dashed',
        borderColor: applied ? 'primary.light' : 'action.disabled',
        opacity: applied ? 1 : 0.75,
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 0,
          '& .MuiAccordionSummary-content': {
            alignItems: 'stretch',
            flexDirection: 'column',
            gap: 0.25,
            m: 0,
            py: 0.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600 }}>
            {`${index + 1}. ${title}`}
          </Typography>
          {step.modifier === 'tiebreak' && (
            <Chip size="small" color="secondary" label="тай-брейк" />
          )}
          {!applied && (
            <Chip size="small" variant="outlined" label="не сработало" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {rub(step.before)} →{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {rub(step.after)}
            </Box>
          </Typography>
        </Box>
        {promotionTitle && (
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ fontWeight: 600, pr: 3 }}
          >
            {promotionTitle}
          </Typography>
        )}
        {step.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ pr: 3 }}
          >
            {step.description}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <DeltaChip delta={step.delta} />
          {step.ref_type === 'promotion' && step.ref_id != null && (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="always"
              onClick={() => onOpenPromotion(step.ref_id as number | string)}
            >
              Перейти к акции →
            </Link>
          )}
          {step.ref_type === 'discount_document' && step.ref_id != null && (
            <Chip
              size="small"
              label={`документ скидки: ${step.ref_id}`}
              variant="outlined"
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const WarehouseCard: React.FunctionComponent<{
  item: PriceTraceItem;
  onOpenPromotion: (id: number | string) => void;
}> = ({ item, onOpenPromotion }) => {
  const warehouse = item.warehouse;
  const promotion: PriceTracePromotion | null | undefined = item.promotion;
  const steps = item.steps || [];
  const offer = rub(item.offer_price);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1">
            {warehouse?.name || warehouse?.vendor_id || 'Склад'}
          </Typography>
          {warehouse?.vendor_id && (
            <Typography variant="caption" color="text.secondary">
              vendor id: {warehouse.vendor_id}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6">{offer}</Typography>
          <Typography variant="caption" color="text.secondary">
            итоговая цена (offer_price)
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Chip
          size="small"
          label={priceTypeLabels[item.price_type || ''] || item.price_type}
          color="primary"
          variant="outlined"
        />
        {promotion?.id != null && (
          <Chip
            size="small"
            color="success"
            label="применена акция"
            onClick={() => onOpenPromotion(promotion.id as number | string)}
            clickable
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Базовая цена клиента: <b style={{ color: 'inherit' }}>{rub(item.client_base_price)}</b>
        {item.discount_value != null && item.discount_value > 0 && (
          <> · скидка: {item.discount_value}%</>
        )}
      </Typography>

      {promotion && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            {promotion.title || 'Акция'}
            {promotion.priority != null && ` · приоритет ${promotion.priority}`}
          </Typography>
          {(promotion.valid_from || promotion.valid_to) && (
            <Typography variant="caption" color="text.secondary">
              {formatDay(promotion.valid_from)} — {formatDay(promotion.valid_to)}
            </Typography>
          )}
        </Box>
      )}

      {item.available_price != null && item.available_price !== item.offer_price && (
        <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
          При выполнении условий «доступной» акции цена составит{' '}
          <b>{rub(item.available_price)}</b>
        </Alert>
      )}

      {steps.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="overline" display="block">
            Маршрут цены (steps)
          </Typography>
          {steps.map((step, index) => (
            <StepRow
              key={step.key || index}
              step={step}
              index={index}
              promotion={item.promotion}
              onOpenPromotion={onOpenPromotion}
            />
          ))}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 1,
              p: 1.5,
              border: 1,
              borderColor: 'success.main',
              borderRadius: 1,
            }}
          >
            <Chip size="small" color="success" label="итог" />
            <Typography sx={{ fontWeight: 700 }}>Итог</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="h6" color="success.main">
              {offer}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

const TraceResult: React.FunctionComponent<{
  data: PriceTraceData;
  onOpenPromotion: (id: number | string) => void;
}> = ({ data, onOpenPromotion }) => {
  const items = data.items || [];

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, rowGap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Товар
            </Typography>
            <Typography>
              {data.productName || '—'}
              {data.productCode != null && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' · код '}
                  {data.productCode}
                </Typography>
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Торговая точка
            </Typography>
            <Typography>{data.outletVendorId || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Дата расчёта
            </Typography>
            <Typography>{data.date || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Складов в регионе
            </Typography>
            <Typography>{items.length}</Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Сплошная рамка — шаг сработал; пунктир — правило рассмотрено и не
          изменило цену. Клик по строке раскрывает детали шага.
        </Typography>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr' },
          gap: 2,
        }}
      >
        {items.map((item, index) => (
          <WarehouseCard
            key={item.warehouse?.vendor_id || index}
            item={item}
            onOpenPromotion={onOpenPromotion}
          />
        ))}
      </Box>
    </Box>
  );
};

export const PriceTracePage: React.FunctionComponent = () => {
  const { query, push } = useRouter();

  const productParam = first(query.product);
  const outletParam = first(query.outlet);
  const warehouseParam = first(query.warehouse);

  const [product, setProduct] = useState(productParam);
  const [outlet, setOutlet] = useState(outletParam);
  const [warehouse, setWarehouse] = useState(warehouseParam);

  const ready = Boolean(productParam && outletParam);

  const priceTraceRequest = usePriceTrace({
    variables: {
      product: productParam,
      outlet: outletParam,
      warehouse: warehouseParam || undefined,
    },
    skip: !ready,
  });

  const applyParams = (values: FormValues) => {
    setProduct(values.product);
    setOutlet(values.outlet);
    setWarehouse(values.warehouse);
    push(routesMap.priceTrace.getRoute(), {
      product: values.product.trim(),
      outlet: values.outlet.trim(),
      warehouse: values.warehouse.trim() || undefined,
    });
  };

  const handleReset = () => {
    setProduct('');
    setOutlet('');
    setWarehouse('');
    push(routesMap.priceTrace.getRoute());
  };

  const openPromotion = (id: number | string) =>
    push(routesMap.promotion.getRoute(id));

  const axiosError = priceTraceRequest.error as AxiosError<{
    errors?: string[];
  }> | null;
  const httpStatus = axiosError?.response?.status;
  const httpErrors = axiosError?.response?.data?.errors || [];
  const envelopeErrors = priceTraceRequest.data?.errors || [];
  const errors = httpErrors.length > 0 ? httpErrors : envelopeErrors;
  const isAccessDenied = httpStatus === 401 || httpStatus === 403;

  const data = priceTraceRequest.data?.data;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Трассировка цены товара
        </Typography>
        <TraceForm
          product={product}
          outlet={outlet}
          warehouse={warehouse}
          onChange={values => {
            setProduct(values.product);
            setOutlet(values.outlet);
            setWarehouse(values.warehouse);
          }}
          onSubmit={() => applyParams({ product, outlet, warehouse })}
          onReset={handleReset}
        />
      </Paper>

      {!ready && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Укажите код товара и vendor id торговой точки — страница покажет, из
            чего сложилась цена на каждом складе.
          </Typography>
        </Paper>
      )}

      {ready && priceTraceRequest.isLoading && (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Paper>
      )}

      {ready && isAccessDenied && (
        <Alert severity="error">
          Нет доступа (HTTP {httpStatus}). Эндпоинт трассировки цены доступен
          ролям менеджера и администратора.
        </Alert>
      )}

      {ready && !priceTraceRequest.isLoading && errors.length > 0 && !data && (
        <Alert severity="warning">
          {errors.map((error, index) => (
            <Box key={index}>{error}</Box>
          ))}
        </Alert>
      )}

      {ready && !priceTraceRequest.isLoading && data && errors.length === 0 && (
        <TraceResult data={data} onOpenPromotion={openPromotion} />
      )}
    </Box>
  );
};
